/** Splits only high-confidence comma-and compound DMI defects. */
export function splitCompoundDmiDefects(defectText: string): string[] {
  const sourceText: string = defectText.trim();
  const candidates: string[] = sourceText
    .split(/,\s+(?:and\s+)?/i)
    .map((candidate: string): string => candidate.trim());
  const areIndependentDmiDefects: boolean =
    candidates.length > 1 &&
    candidates.every((candidate: string): boolean => /\bDMI[.!?]?\s*$/i.test(candidate));
  if (!areIndependentDmiDefects) return sourceText ? [sourceText] : [];
  return candidates.map((candidate: string): string =>
    /[.!?]$/.test(candidate) ? candidate : `${candidate}.`,
  );
}

/** Splits each supplied triage defect while preserving its source order. */
export function splitCompoundDmiDefectList(defectTexts: string[]): string[] {
  return defectTexts.reduce<string[]>(
    (defects: string[], defectText: string): string[] => [
      ...defects,
      ...splitCompoundDmiDefects(defectText),
    ],
    [],
  );
}

/** Creates a stable comparison key without changing the displayed defect text. */
export function defectComparisonKey(defectText: string): string {
  return defectText
    .normalize('NFC')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, '-')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.!?]+$/, '')
    .toLocaleLowerCase();
}

const IDENTITY_TOKEN_ALIASES: Readonly<Record<string, string>> = {
  eng: 'engine',
  ind: 'indication',
  lh: 'left',
  press: 'pressure',
  qty: 'quantity',
  rh: 'right',
  temp: 'temperature',
};

/** Converts a defect description to the identity-bearing tokens used for duplicate checks. */
function defectIdentityTokens(defectText: string): Set<string> {
  const numberNormalizedText: string = defectComparisonKey(defectText)
    .replace(/#\s*(\d+)/g, ' $1 ')
    .replace(/\b(?:no|number)\.?\s*(\d+)\b/g, ' $1 ');
  const rawTokens: RegExpMatchArray | null =
    numberNormalizedText.match(/[a-z0-9]+(?:-[a-z0-9]+)*/g);
  return new Set<string>(
    (rawTokens || [])
      .map((token: string): string => IDENTITY_TOKEN_ALIASES[token] || token)
      .filter((token: string): boolean => token !== 'dmi'),
  );
}

/** Returns numeric/side identifiers that must agree before two defects can match. */
function defectIdentifiers(identityTokens: ReadonlySet<string>): Set<string> {
  return new Set<string>(
    Array.from(identityTokens).filter(
      (token: string): boolean => /^\d+$/.test(token) || token === 'left' || token === 'right',
    ),
  );
}

/** Returns true when two populated identifier sets share no common value. */
function hasConflictingIdentifiers(
  candidateTokens: ReadonlySet<string>,
  existingTokens: ReadonlySet<string>,
): boolean {
  const candidateIdentifiers: Set<string> = defectIdentifiers(candidateTokens);
  const existingIdentifiers: Set<string> = defectIdentifiers(existingTokens);
  if (candidateIdentifiers.size === 0 || existingIdentifiers.size === 0) return false;
  return !Array.from(candidateIdentifiers).some((identifier: string): boolean =>
    existingIdentifiers.has(identifier),
  );
}

/**
 * Determines whether wording identifies the same defect while retaining a
 * deliberately high bar: every token in a concise description, or at least
 * 80% of the shorter description, must agree. Differing side/number markers
 * can never match.
 */
function hasEquivalentDefectIdentity(candidateText: string, existingText: string): boolean {
  const candidateTokens: Set<string> = defectIdentityTokens(candidateText);
  const existingTokens: Set<string> = defectIdentityTokens(existingText);
  const shorterTokenCount: number = Math.min(candidateTokens.size, existingTokens.size);
  if (shorterTokenCount < 2 || hasConflictingIdentifiers(candidateTokens, existingTokens))
    return false;
  const matchingTokenCount: number = Array.from(candidateTokens).filter((token: string): boolean =>
    existingTokens.has(token),
  ).length;
  const requiredMatchingTokens: number = Math.ceil(shorterTokenCount * 0.8);
  return matchingTokenCount >= requiredMatchingTokens;
}

/**
 * Returns true for an exact or high-confidence equivalent existing defect.
 * Displayed text is never changed; this is comparison-only normalization.
 */
export function isDuplicateDefect(
  defectText: string,
  existingDefectTexts: readonly string[],
): boolean {
  const defectKey: string = defectComparisonKey(defectText);
  return existingDefectTexts.some(
    (existingText: string): boolean =>
      defectKey === defectComparisonKey(existingText) ||
      hasEquivalentDefectIdentity(defectText, existingText),
  );
}

/**
 * Returns true only when every explicit component of a compound notice is
 * already represented by an exact or high-confidence equivalent event defect.
 */
export function isCompoundDefectCovered(
  defectText: string,
  existingDefectTexts: readonly string[],
): boolean {
  const compoundParts: string[] = defectText
    .split(/(?:,\s*(?:and\s+)?|\s+(?:and|&)\s+)/i)
    .map((part: string): string => part.trim())
    .filter(Boolean);
  return (
    compoundParts.length > 1 &&
    compoundParts.every((part: string): boolean => isDuplicateDefect(part, existingDefectTexts))
  );
}
